import { useEffect, useState } from "react";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const triggerCls =
  "w-full flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-foreground hover:bg-white/8 focus:outline-none focus:border-gold transition-colors";

const formatDateLabel = (yyyyMmDd) => {
  if (!yyyyMmDd) return "Select date";
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const DatePicker = ({ value, onChange, testId, minDate }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Lower bound — caller may pass minDate (e.g. pickup date) to prevent
  // returns from being scheduled before the outbound leg.
  const lowerBound = minDate ? new Date(`${minDate}T00:00:00`) : today;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={triggerCls}
          aria-label="Pick date"
        >
          <span className={value ? "text-foreground" : "text-foreground/40"}>
            {formatDateLabel(value)}
          </span>
          <CalendarIcon className="h-4 w-4 text-foreground/60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-[#0a1830] border-white/10"
        align="start"
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (d) {
              onChange(toYmd(d));
              setOpen(false);
            }
          }}
          disabled={(d) => d < lowerBound}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

// Time stored internally as "HH:MM" 24h string for backend compatibility
const parseTime = (hhmm) => {
  if (!hhmm) return { hour12: 12, minute: 0, period: "AM" };
  const [hStr, mStr] = hhmm.split(":");
  const h24 = parseInt(hStr, 10);
  const minute = parseInt(mStr, 10);
  const period = h24 >= 12 ? "PM" : "AM";
  let hour12 = h24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute, period };
};

const buildTime = (hour12, minute, period) => {
  let h24 = hour12 % 12;
  if (period === "PM") h24 += 12;
  return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const selectCls =
  "appearance-none rounded-lg bg-white/5 border border-white/10 px-2 py-2 text-sm text-foreground focus:outline-none focus:border-gold transition-colors cursor-pointer";

const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
const minutes = Array.from({ length: 60 }, (_, i) => i);

const formatTimeLabel = (hhmm) => {
  if (!hhmm) return "Select time";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

export const TimePicker = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  // Local "draft" state — selection only commits when the user clicks OK.
  // This fixes the bug where the initially-displayed value (e.g. 12:00 AM)
  // could not be selected because no change event ever fired.
  const initial = parseTime(value);
  const [hour, setHour] = useState(initial.hour12);
  const [minute, setMinute] = useState(initial.minute);
  const [period, setPeriod] = useState(initial.period);

  // Reset the draft to the current value every time the popover opens, so
  // closing without confirming truly discards the changes.
  useEffect(() => {
    if (open) {
      const p = parseTime(value);
      setHour(p.hour12);
      setMinute(p.minute);
      setPeriod(p.period);
    }
  }, [open, value]);

  const confirm = () => {
    onChange(buildTime(hour, minute, period));
    setOpen(false);
  };

  const draftLabel = `${hour}:${String(minute).padStart(2, "0")} ${period}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={triggerCls}
          aria-label="Pick time"
        >
          <span className={value ? "text-foreground" : "text-foreground/40"}>
            {formatTimeLabel(value)}
          </span>
          <Clock className="h-4 w-4 text-foreground/60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto p-4 bg-[#0d2042] border border-white/10 rounded-2xl shadow-2xl"
      >
        <div className="text-[0.65rem] uppercase tracking-[0.2em] text-foreground/55 mb-1 text-center">
          Pick a time
        </div>
        <div className="text-center font-display text-lg text-gold mb-3">
          {draftLabel}
        </div>
        <div className="flex items-center justify-center gap-2">
          <select
            aria-label="Hour"
            value={hour}
            onChange={(e) => setHour(parseInt(e.target.value, 10))}
            className={selectCls}
          >
            {hours12.map((h) => (
              <option key={h} value={h} className="bg-[#0a1830]">
                {h}
              </option>
            ))}
          </select>
          <span className="text-gold font-semibold">:</span>
          <select
            aria-label="Minute"
            value={minute}
            onChange={(e) => setMinute(parseInt(e.target.value, 10))}
            className={selectCls}
          >
            {minutes.map((m) => (
              <option key={m} value={m} className="bg-[#0a1830]">
                {String(m).padStart(2, "0")}
              </option>
            ))}
          </select>
          <div className="ml-1 inline-flex rounded-full bg-white/5 border border-white/10 p-0.5">
            {["AM", "PM"].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  period === p
                    ? "bg-gold text-[#0a1830]"
                    : "text-foreground/65 hover:text-foreground"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          data-testid={testId ? `${testId}-ok` : undefined}
          onClick={confirm}
          className="mt-4 w-full rounded-full bg-gold text-[#0a1830] px-4 py-2 text-sm font-semibold hover:bg-[#f0c878] transition-colors"
        >
          OK
        </button>
      </PopoverContent>
    </Popover>
  );
};
